import { CSSProperties, ReactNode, useEffect, useRef } from 'react';
import { useInView } from 'react-intersection-observer';

export interface VideoProps {
  src: string;
  className?: string;
  mode?: 'autoplay' | 'hover';
  contained?: boolean;
  loop?: boolean;
  inViewThreshold?: number | number[];
  onLoadedData?: () => void;
  onError?: () => void;
  onEnded?: () => void;
  children?: ReactNode;
  style?: CSSProperties;
  crossOrigin?: 'anonymous' | 'use-credentials';
}

export default function Video({
  src,
  className,
  mode = 'autoplay',
  onLoadedData,
  onError,
  onEnded,
  children,
  contained = false,
  loop = true,
  inViewThreshold = 0.5,
  style,
  crossOrigin,
}: VideoProps) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const { ref: inViewRef, inView } = useInView({
    threshold:
      typeof inViewThreshold === 'number' ? [inViewThreshold] : inViewThreshold,
    skip: mode !== 'autoplay',
  });

  useEffect(() => {
    if (mode !== 'autoplay') return;
    if (inView) {
      videoRef.current?.play();
    } else {
      videoRef.current?.pause();
    }
  }, [inView, mode]);

  return (
    <div ref={inViewRef} className={className} style={style}>
      <video
        ref={videoRef}
        onMouseEnter={() => {
          if (mode === 'hover') {
            videoRef.current?.play();
          }
        }}
        onMouseLeave={() => {
          if (mode === 'hover') {
            videoRef.current?.pause();
          }
        }}
        src={src}
        crossOrigin={crossOrigin}
        className={`h-full w-full rounded-[inherit] object-${
          contained ? 'contain' : 'cover'
        }`}
        muted
        playsInline
        loop={loop}
        disablePictureInPicture
        autoPlay={mode === 'autoplay'}
        onContextMenu={(e) => e.preventDefault()}
        onLoadedData={() => onLoadedData?.()}
        onError={() => onError?.()}
        onEnded={() => onEnded?.()}
      >
        {children}
      </video>
    </div>
  );
}
